import { createContext, useContext, useEffect, useState, type ReactNode, type CSSProperties } from 'react';
import type {
  DashboardFilterPanel,
  DashboardSlicer,
  SemanticFilter,
} from '@lognai/plugin-analytics';
import { Button, Input, Sheet } from '@lognai/synapse-design-system-v2';
import { Filter, X } from '@lognai/synapse-design-system-v2/icons';
import { getApiHeaders, getCompanyId } from '@lognai/plugin-sdk';
import { getFilterValues, type FilterValuesResult } from './api';
import { dateSlicerFilters, slicerDateBounds, toggleDashboardFilter, filterDisplayValue } from './dashboard-filters';
import './dashboard-filters.css';

type Request = Parameters<typeof getFilterValues>[0][number];
const valueCache = new Map<string, { at: number; value: FilterValuesResult }>();
async function cachedValues(items: Request[]) {
  const scope = JSON.stringify([getCompanyId(), getApiHeaders()]);
  const key = (item: Request) => JSON.stringify([scope, item]);
  const missing = items.filter(
    (item) => !valueCache.has(key(item)) || Date.now() - valueCache.get(key(item))!.at > 60_000
  );
  if (missing.length) {
    const result = await getFilterValues(missing);
    result.results.forEach((value, index) => {
      if (value.ok) valueCache.set(key(missing[index]), { at: Date.now(), value });
    });
    const values = items.map(
      (item) =>
        result.results.find((r) => r.member === item.member) ?? valueCache.get(key(item))!.value
    );
    if (valueCache.size > 200) valueCache.clear();
    return values;
  }
  return items.map((item) => valueCache.get(key(item))!.value);
}

function DateSlicer({
  slicer,
  filters,
  onChange,
  disabled,
}: {
  slicer: DashboardSlicer;
  filters: SemanticFilter[];
  onChange: (f: SemanticFilter[]) => void;
  disabled: boolean;
}) {
  const bounds = slicerDateBounds(filters, slicer.member);
  const [draft, setDraft] = useState(bounds);
  useEffect(() => setDraft(bounds), [bounds[0], bounds[1]]);
  const update = (index: number, value: string) => {
    const next = [...draft] as [string, string];
    next[index] = value;
    setDraft(next);
    if (next[0] && next[1] && next[0] > next[1]) return;
    onChange([
      ...filters.filter((f) => f.member !== slicer.member),
      ...dateSlicerFilters(slicer.member, ...next),
    ]);
  };
  return (
    <>
      <div className="grid gap-2">
        <label className="grid gap-1 text-xs">
          From
          <Input
            aria-label={`${slicer.label} from`}
            type="date"
            value={draft[0]}
            disabled={disabled}
            onChange={(e) => update(0, e.target.value)}
            onInput={(e) => update(0, e.currentTarget.value)}
          />
        </label>
        <label className="grid gap-1 text-xs">
          To
          <Input
            aria-label={`${slicer.label} to`}
            type="date"
            value={draft[1]}
            disabled={disabled}
            onChange={(e) => update(1, e.target.value)}
            onInput={(e) => update(1, e.currentTarget.value)}
          />
        </label>
      </div>
      {draft[0] && draft[1] && draft[0] > draft[1] && (
        <p role="alert" className="text-xs text-destructive">
          Start must precede end.
        </p>
      )}
    </>
  );
}

function FilterControls({
  panel,
  filters,
  onChange,
  disabled,
}: {
  panel: DashboardFilterPanel;
  filters: SemanticFilter[];
  onChange: (f: SemanticFilter[]) => void;
  disabled: boolean;
}) {
  const [queries, setQueries] = useState<Record<string, { search: string; offset: number }>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, FilterValuesResult>>({});
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const fields = panel.slicers.filter((s) => s.control !== 'date_range' && s.member);
  const context = JSON.stringify(panel.contextualValues ? filters : []);
  const fieldKey = JSON.stringify(fields.map((s) => [s.member, s.order]));
  useEffect(() => {
    setQueries((q) =>
      Object.fromEntries(Object.entries(q).map(([m, v]) => [m, { ...v, offset: 0 }]))
    );
    setResults({});
  }, [context, fieldKey]);
  const requestKey = JSON.stringify(
    fields.map((s) => ({
      member: s.member,
      order: s.order,
      search: queries[s.member]?.search ?? '',
      offset: queries[s.member]?.offset ?? 0,
      limit: 50,
      filters: panel.contextualValues ? filters.filter((f) => f.member !== s.member) : [],
    }))
  );
  useEffect(() => {
    let live = true;
    const items: Request[] = JSON.parse(requestKey);
    if (!items.length) {
      setResults({});
      return;
    }
    setBusy(true);
    const timer = window.setTimeout(() => {
      void cachedValues(items)
        .then((values) => {
          if (!live) return;
          setResults((current) =>
            Object.fromEntries(
              values.map((value, index) => {
                const previous = items[index].offset ? (current[value.member]?.values ?? []) : [];
                return [
                  value.member,
                  {
                    ...value,
                    values: [
                      ...new Map([...previous, ...value.values].map((v) => [v.value, v])).values(),
                    ],
                  },
                ];
              })
            )
          );
        })
        .catch(() => {
          if (live)
            setResults(
              Object.fromEntries(
                items.map((item) => [
                  item.member,
                  {
                    member: item.member,
                    ok: false,
                    error: 'Available values could not be loaded.',
                    values: [],
                    hasMore: false,
                  },
                ])
              )
            );
        })
        .finally(() => {
          if (live) setBusy(false);
        });
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [requestKey, retry]);
  return (
    <div className="dashboard-filter-controls">
      {panel.slicers.map((slicer) => {
        const selected =
          filters.find((f) => f.member === slicer.member && f.operator === 'equals')?.values ?? [];
        const result = results[slicer.member];
        const isExpanded = expanded[slicer.member] || slicer.control === 'multi_select';
        const candidates = isExpanded
          ? (result?.values ?? [])
          : (result?.values ?? []).slice(0, slicer.visibleValues);
        const values = [
          ...candidates,
          ...selected
            .filter((value) => !candidates.some((item) => item.value === value))
            .map((value) => ({ value, count: undefined as number | undefined })),
        ];
        return (
          <section
            key={slicer.id}
            className="dashboard-filter-group border-t border-border py-3"
            aria-label={`${slicer.label} filter`}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="min-w-0 text-xs font-semibold">{slicer.label}</h3>
              {filters.some((f) => f.member === slicer.member) && (
                <Button
                  size="xs"
                  variant="ghost"
                  disabled={disabled}
                  aria-label={`Clear ${slicer.label}`}
                  onClick={() => onChange(filters.filter((f) => f.member !== slicer.member))}
                >
                  <X size={12} />
                </Button>
              )}
            </div>
            {slicer.control === 'date_range' ? (
              <DateSlicer
                slicer={slicer}
                filters={filters}
                onChange={onChange}
                disabled={disabled}
              />
            ) : (
              <>
                {isExpanded && (
                  <Input
                    className="mb-2"
                    aria-label={`Search ${slicer.label}`}
                    placeholder="Search values…"
                    value={queries[slicer.member]?.search ?? ''}
                    disabled={disabled}
                    onChange={(e) =>
                      setQueries((q) => ({
                        ...q,
                        [slicer.member]: { search: e.target.value, offset: 0 },
                      }))
                    }
                  />
                )}
                <div
                  className={
                    slicer.control === 'chips'
                      ? 'flex flex-wrap gap-1'
                      : 'grid max-h-60 gap-1 overflow-auto'
                  }
                  aria-busy={busy}
                >
                  {values.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      aria-pressed={selected.includes(item.value)}
                      disabled={
                        disabled || (!selected.includes(item.value) && selected.length >= 50)
                      }
                      className={`dashboard-filter-value rounded border px-2 py-1 text-left text-xs ${selected.includes(item.value) ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-surface text-muted-foreground'}`}
                      onClick={() =>
                        onChange(
                          toggleDashboardFilter(filters, {
                            member: slicer.member,
                            operator: 'equals',
                            values: [item.value],
                          })
                        )
                      }
                    >
                      {slicer.control === 'multi_select' && (
                        <span aria-hidden="true" className="mr-2">
                          {selected.includes(item.value) ? '☑' : '☐'}
                        </span>
                      )}
                      {item.value}
                      {panel.showCounts && item.count != null && (
                        <span className="ml-1 opacity-75">({item.count.toLocaleString()})</span>
                      )}
                    </button>
                  ))}
                </div>
                {busy && !result && (
                  <p className="text-xs text-muted-foreground">Loading values…</p>
                )}
                {result && !result.ok && (
                  <div role="alert" className="text-xs text-destructive">
                    {result.error}
                    <Button size="xs" variant="ghost" onClick={() => setRetry((n) => n + 1)}>
                      Retry
                    </Button>
                  </div>
                )}
                {result?.ok && !values.length && !busy && (
                  <p className="text-xs text-muted-foreground">No matching values.</p>
                )}
                {!isExpanded &&
                  ((result?.values.length ?? 0) > slicer.visibleValues || result?.hasMore) && (
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={disabled}
                      onClick={() => setExpanded((e) => ({ ...e, [slicer.member]: true }))}
                    >
                      Search / show all
                    </Button>
                  )}
                {isExpanded && result?.hasMore && (
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={disabled || busy}
                    onClick={() =>
                      setQueries((q) => ({
                        ...q,
                        [slicer.member]: {
                          search: q[slicer.member]?.search ?? '',
                          offset: result.nextOffset ?? 50,
                        },
                      }))
                    }
                  >
                    Load more
                  </Button>
                )}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

type FilterSystemProps = {
  panel?: DashboardFilterPanel;
  filters: SemanticFilter[];
  onChange: (filters: SemanticFilter[]) => void;
  onClear: () => void;
  disabled?: boolean;
  labelForMember?: (member: string) => string;
  resetKey?: string;
  children: ReactNode;
};
type FilterSystemState = Omit<FilterSystemProps, 'children'> & {
  collapsed: boolean;
  setCollapsed: (value: boolean | ((value: boolean) => boolean)) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
};
const FilterSystemContext = createContext<FilterSystemState | null>(null);
function useFilterSystem() {
  const value = useContext(FilterSystemContext);
  if (!value) throw new Error('Dashboard filter parts must be inside DashboardFilters');
  return value;
}

/** One state owner for the toolbar, field controls, and responsive drawer. */
export function DashboardFilters({ children, ...props }: FilterSystemProps) {
  const [collapsed, setCollapsed] = useState(props.panel?.startCollapsed ?? false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setCollapsed(props.panel?.startCollapsed ?? false);
    setOpen(false);
  }, [props.resetKey, props.panel?.startCollapsed, props.panel?.position]);
  return <FilterSystemContext.Provider value={{ ...props, collapsed, setCollapsed, open, setOpen }}>{children}</FilterSystemContext.Provider>;
}

export function DashboardFilterToolbar({ dateControl, dateActive = false, status, className = '', children }: {
  dateControl?: ReactNode;
  dateActive?: boolean;
  status?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  const { panel, filters, onChange, onClear, disabled, labelForMember, collapsed, setCollapsed, open, setOpen } = useFilterSystem();
  const hasControls = !!panel?.enabled && !!panel.slicers.length;
  const hasDate = panel?.showDatePreset !== false && !!dateControl;
  const label = (member: string) => panel?.slicers.find(s => s.member === member)?.label ?? labelForMember?.(member) ?? member;
  return (
    <section aria-label="Dashboard filter toolbar" className={`dashboard-filter-toolbar flex w-full flex-wrap items-center gap-2 rounded-lg border border-border bg-surface px-4 py-3 sm:px-6 ${className}`}>
      <span className="inline-flex items-center gap-1.5 text-sm font-semibold"><Filter size={14} />Filters</span>
      {hasDate && dateControl}
      {hasControls && <>
        <span className="dashboard-filter-desktop-toggle"><Button size="sm" variant="outline" aria-expanded={panel?.position === 'drawer' ? open : !collapsed} onClick={() => panel?.position === 'drawer' ? setOpen(true) : setCollapsed(value => !value)}>{panel?.position === 'drawer' || collapsed ? 'Show filters' : 'Hide filters'}</Button></span>
        <span className="dashboard-filter-mobile-toggle"><Button size="sm" variant="outline" aria-expanded={open} onClick={() => setOpen(true)}>Show filters</Button></span>
      </>}
      {children}
      {filters.length > 0 && <div className="flex flex-wrap items-center gap-1.5 border-l border-border pl-2">
        {filters.map((filter, index) => <button key={`${filter.member}:${index}`} type="button" disabled={disabled} aria-label={`Remove ${label(filter.member)} filter`} className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs" onClick={() => onChange(filters.filter(item => item.member !== filter.member))}>{label(filter.member)}: {filterDisplayValue(filter)}<X size={12} /></button>)}
      </div>}
      {(hasDate || hasControls || filters.length > 0) && <Button size="xs" variant="ghost" disabled={disabled || (!filters.length && !(hasDate && dateActive))} onClick={onClear}>Clear all</Button>}
      {status}
    </section>
  );
}

export function DashboardFilterContent({ children }: { children: ReactNode }) {
  const { panel, filters, onChange, disabled = false, collapsed, open, setOpen } = useFilterSystem();
  if (!panel?.enabled || !panel.slicers.length) return <>{children}</>;
  const controls = <FilterControls panel={panel} filters={filters} onChange={onChange} disabled={disabled} />;
  return (
    <div className={`dashboard-filter-layout dashboard-filter-layout--${panel.position}${collapsed ? ' dashboard-filter-layout--collapsed' : ''}`} style={{ '--filter-width': `${panel.width}px` } as CSSProperties}>
      {!collapsed && panel.position !== 'drawer' && <aside className="dashboard-filter-rail rounded border border-border bg-surface p-3" aria-label="Dashboard filters">{controls}</aside>}
      <div className="dashboard-filter-content min-w-0 space-y-4">{children}</div>
      <Sheet open={open} onOpenChange={setOpen}>
        <Sheet.Content side={panel.position === 'left' ? 'left' : 'right'} className="flex flex-col">
          <Sheet.Header><Sheet.Title>Filter controls</Sheet.Title><Sheet.Description>Selections update the dashboard.</Sheet.Description></Sheet.Header>
          <div className="min-h-0 flex-1 overflow-auto p-3">{open && controls}</div>
          <Sheet.Footer><Button onClick={() => setOpen(false)}>Done</Button></Sheet.Footer>
        </Sheet.Content>
      </Sheet>
    </div>
  );
}
